import { z } from 'zod';
import type {
  AgentMessageResponse,
  AgentRunSummary,
  KernelTelemetrySummary,
  ToolCallRunResponse,
} from '@erp/rpc';
import type { Span } from 'src/components/agent-trace';
import {
  isUnfinishedTask,
  taskDetailSpans,
} from 'src/components/agent-trace/procedureOwnership';
import {
  buildSpans,
  operatorFinalResponse,
} from 'src/components/agent-trace/utils';
import { timelineSessionMedia } from 'src/components/common/agentSessionScreenshots';
import { hasKernelTelemetry } from 'src/components/common/kernelTelemetryDisplay';
import type { RunTimeline } from 'src/components/agent-trace/useRunSpans';
import { formatDurationMs as formatRunDuration } from 'src/utils/formatDurationMs';

/**
 * Pure helpers behind the agent page's history and stat strip. Kept free of
 * React so the arithmetic and formatting stay unit-testable.
 */

/** A run's duration is stored in milliseconds. Humanised for a list row. */
export { formatRunDuration };

/** How many newest runs the success-rate tile looks at. */
export const SUCCESS_RATE_WINDOW = 10;

/** Anchor for the host tab that holds history + the task queue. */
export const AGENT_PAGE_TASKS_ID = 'agent-page-tasks';

/**
 * `finishReason` of a task-loop run the start gate refused. The launcher had
 * already created the row, so it was settled without ever doing work.
 */
export const RUN_NOT_STARTED = 'not_started';

/** A run that never ran: the coordinator refused to start it. */
export function runDidNotStart(
  run: Pick<AgentRunSummary, 'finishReason'>,
): boolean {
  return run.finishReason === RUN_NOT_STARTED;
}

/**
 * A skipped check: refused while the agent was merely waiting (start
 * conditions not met, its resource busy), so it settled `completed`. Not
 * skipped: a refusal that failed (a person must act on it) and a run a person
 * started by hand. The History filter hides exactly these; twin of the
 * server's `skippedRunWhere` (AgentRunService.ts) — keep them in step.
 */
export function runWasSkipped(
  run: Pick<AgentRunSummary, 'finishReason' | 'status' | 'triggerKind'>,
): boolean {
  return (
    runDidNotStart(run) &&
    run.status === 'completed' &&
    run.triggerKind !== 'manual'
  );
}

export interface RunStats {
  /** Runs summarised (the history rows currently loaded). */
  total: number;
  completed: number;
  failed: number;
  running: number;
  /** Rows the start gate refused; neither a success nor the agent's failure. */
  notStarted: number;
  /**
   * completed / (completed + failed) among the newest
   * {@link SUCCESS_RATE_WINDOW} runs, or null when none of those have finished.
   */
  successRate: number | null;
  /** How many newest runs the success rate considered (≤ {@link SUCCESS_RATE_WINDOW}). */
  successRateSample: number;
  /** Mean duration of finished runs in that same window (ms), or null. */
  averageDurationMs: number | null;
}

export const runDetailTabSchema = z.enum(['details', 'actions', 'trace']);
export type RunDetailTab = z.infer<typeof runDetailTabSchema>;

export function isRunDetailTab(value: string): value is RunDetailTab {
  return runDetailTabSchema.safeParse(value).success;
}

/** Top-level pane on an orchestrated agent page: history+run, or the queue. */
export const agentPageWorkPaneSchema = z.enum(['runs', 'tasks']);
export type AgentPageWorkPane = z.infer<typeof agentPageWorkPaneSchema>;

export function isAgentPageWorkPane(value: string): value is AgentPageWorkPane {
  return agentPageWorkPaneSchema.safeParse(value).success;
}

/** Keep the Details pane selected once Actions / Full Trace are gone. */
export function visibleRunDetailTab(
  tab: RunDetailTab,
  showTrace: boolean,
): RunDetailTab {
  return showTrace ? tab : 'details';
}

/**
 * The open-tasks tile opens the queue. Picking a run in history (when that
 * list is visible) returns to run details.
 */
export function workPaneForAction(
  action: 'open-tasks' | 'select-run',
): AgentPageWorkPane {
  return action === 'open-tasks' ? 'tasks' : 'runs';
}

/** Finished runs open on what they produced; a live run opens on its actions. */
export function defaultRunDetailTab(
  run: Pick<AgentRunSummary, 'status'>,
): RunDetailTab {
  return run.status === 'running' ? 'actions' : 'details';
}

/**
 * Roll the loaded history up into the numbers the stat strip shows. Counts
 * describe the rows on screen. Success rate and average duration only look at
 * the newest {@link SUCCESS_RATE_WINDOW} so loading older history cannot move
 * the rate.
 */
export function summarizeRuns(runs: readonly AgentRunSummary[]): RunStats {
  let completed = 0;
  let failed = 0;
  let running = 0;
  let notStarted = 0;
  for (const run of runs) {
    if (runDidNotStart(run)) notStarted += 1;
    else if (run.status === 'completed') completed += 1;
    else if (run.status === 'failed') failed += 1;
    else running += 1;
  }
  const windowed = runs.slice(0, SUCCESS_RATE_WINDOW);
  const windowStats = summarizeFinishedWindow(windowed);
  return {
    total: runs.length,
    completed,
    failed,
    running,
    notStarted,
    successRate: windowStats.successRate,
    successRateSample: windowStats.sample,
    averageDurationMs: windowStats.averageDurationMs,
  };
}

/**
 * Runs that never started say nothing about how well the agent works, so
 * they neither count as successes nor as failures, do not dilute duration,
 * and are left out of the sample the strip names.
 */
function summarizeFinishedWindow(
  runs: readonly AgentRunSummary[],
): Pick<RunStats, 'successRate' | 'averageDurationMs'> & { sample: number } {
  const started = runs.filter((run) => !runDidNotStart(run));
  const completed = started.filter((run) => run.status === 'completed').length;
  const failed = started.filter((run) => run.status === 'failed').length;
  const durations = started
    .filter((run) => run.status !== 'running')
    .map((run) => run.duration)
    .filter(
      (duration): duration is number => duration != null && duration >= 0,
    );
  const finished = completed + failed;
  return {
    successRate: finished > 0 ? completed / finished : null,
    averageDurationMs: average(durations),
    sample: started.length,
  };
}

function average(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

/**
 * Which run the page should open with: the one that is running, else the
 * newest. `null` while the list is empty. Used only until the user picks one.
 */
export function defaultSelectedRunId(
  runs: readonly AgentRunSummary[],
): string | null {
  return (
    runs.find((run) => run.status === 'running')?.id ?? runs[0]?.id ?? null
  );
}

/**
 * The run the detail card shows: the user's pick while it is still in the
 * loaded list, else the page default (`defaultSelectedRunId`).
 */
export function selectRun(
  runs: readonly AgentRunSummary[],
  pickedRunId: string | null,
): AgentRunSummary | null {
  const picked = pickedRunId
    ? runs.find((run) => run.id === pickedRunId)
    : undefined;
  if (picked) return picked;
  const defaultId = defaultSelectedRunId(runs);
  return runs.find((run) => run.id === defaultId) ?? null;
}

export interface OpenTaskPreviewItem {
  id: string;
  title: string;
}

/** One bucket of the tile: the true count, and the loaded tasks to list. */
export interface OpenTaskGroup {
  count: number;
  tasks: OpenTaskPreviewItem[];
}

export interface OpenTaskPreview {
  /** Open tasks currently assigned to the agent — the tile's number. */
  count: number;
  running: OpenTaskGroup;
  queued: OpenTaskGroup;
  needsInput: OpenTaskGroup;
  /** needs_input tasks the agent escalated that now sit with a person. */
  handedOff: OpenTaskGroup;
}

/** Server-side counts per bucket (`tasks.agentOpenCounts`). */
export interface OpenTaskCounts {
  running: number;
  queued: number;
  needsInput: number;
  handedOff: number;
}

interface OpenTaskRow {
  id: string;
  title: string;
  assigneeAgentDefinitionId: string | null;
}

function previewItems(tasks: readonly OpenTaskRow[]): OpenTaskPreviewItem[] {
  return tasks.map((task) => ({ id: task.id, title: task.title }));
}

/**
 * Open tasks for the stat tile. The board also loads tasks the agent escalated
 * to a person; those are split out as handed off rather than counted as the
 * agent's own. Counts come from the server so they do not stop at the rows the
 * board loaded; until they arrive, the loaded rows stand in.
 */
export function openTaskPreview(
  grouped: {
    active: readonly OpenTaskRow[];
    queue: readonly OpenTaskRow[];
    needsInput: readonly OpenTaskRow[];
  },
  agentDefinitionId: string,
  counts: OpenTaskCounts | undefined,
): OpenTaskPreview {
  const own = (task: OpenTaskRow) =>
    task.assigneeAgentDefinitionId === agentDefinitionId;
  const running = previewItems(grouped.active.filter(own));
  const queued = previewItems(grouped.queue.filter(own));
  const needsInput = previewItems(grouped.needsInput.filter(own));
  const handedOff = previewItems(
    grouped.needsInput.filter((task) => !own(task)),
  );
  const group = (
    tasks: OpenTaskPreviewItem[],
    count: number | undefined,
  ): OpenTaskGroup => ({ count: count ?? tasks.length, tasks });
  const result = {
    running: group(running, counts?.running),
    queued: group(queued, counts?.queued),
    needsInput: group(needsInput, counts?.needsInput),
    handedOff: group(handedOff, counts?.handedOff),
  };
  return {
    count: result.running.count + result.queued.count + result.needsInput.count,
    ...result,
  };
}

export interface RunSessionMedia {
  sessionId: string | null;
  recordingDocumentId: string | null;
  kernelTelemetryDocumentId: string | null;
  kernelTelemetrySummary: KernelTelemetrySummary | null;
  initialScreenshotUrl: string | null;
  screenshotToolCalls: ToolCallRunResponse[];
  toolCalls: ToolCallRunResponse[];
  /** Anything at all to put in a session viewer. */
  hasMedia: boolean;
}

/**
 * What a session viewer for the run would show. A live session (from the
 * running run) beats the one recorded in the timeline.
 */
type RunSessionTimeline =
  | (Pick<RunTimeline, 'toolCalls' | 'messages' | 'recordingDocumentId'> &
      Partial<
        Pick<
          RunTimeline,
          | 'recordingToolCalls'
          | 'initialScreenshotSignedUrl'
          | 'kernelTelemetryDocumentId'
          | 'kernelTelemetrySummary'
        >
      >)
  | undefined;

export function resolveRunSessionMedia({
  timeline,
  liveSessionId,
  isRunning,
}: {
  timeline: RunSessionTimeline;
  liveSessionId: string | null | undefined;
  isRunning: boolean;
}): RunSessionMedia {
  const fields = collectRunSessionMediaFields({
    timeline,
    liveSessionId,
    isRunning,
  });
  return { ...fields, hasMedia: runSessionHasMedia(fields) };
}

export function runSessionHasMedia(
  media: Omit<RunSessionMedia, 'hasMedia' | 'toolCalls'>,
): boolean {
  return (
    media.sessionId !== null ||
    media.recordingDocumentId !== null ||
    hasKernelTelemetry(media) ||
    media.initialScreenshotUrl !== null ||
    media.screenshotToolCalls.length > 0
  );
}

function collectRunSessionMediaFields({
  timeline,
  liveSessionId,
  isRunning,
}: {
  timeline: RunSessionTimeline;
  liveSessionId: string | null | undefined;
  isRunning: boolean;
}): Omit<RunSessionMedia, 'hasMedia'> {
  const toolCalls = listTimelineToolCalls(timeline);
  const recorded = timelineSessionMedia(timeline ? { toolCalls } : undefined);
  return {
    sessionId: sessionIdForRun(isRunning, liveSessionId, recorded.sessionId),
    screenshotToolCalls: screenshotToolCallsForTimeline(
      timeline,
      recorded.screenshotToolCalls,
    ),
    toolCalls,
    ...timelineMediaIds(timeline),
  };
}

function listTimelineToolCalls(
  timeline: RunSessionTimeline,
): ToolCallRunResponse[] {
  if (!timeline) return [];
  return [...timeline.toolCalls];
}

function sessionIdForRun(
  isRunning: boolean,
  liveSessionId: string | null | undefined,
  recordedSessionId: string | null,
): string | null {
  if (!isRunning) return null;
  if (typeof liveSessionId === 'string' && liveSessionId.length > 0) {
    return liveSessionId;
  }
  return recordedSessionId;
}

function screenshotToolCallsForTimeline(
  timeline: RunSessionTimeline,
  fallback: ToolCallRunResponse[],
): ToolCallRunResponse[] {
  if (!timeline || timeline.recordingToolCalls == null) return fallback;
  return timeline.recordingToolCalls;
}

function timelineMediaIds(
  timeline: RunSessionTimeline,
): Pick<
  RunSessionMedia,
  | 'recordingDocumentId'
  | 'kernelTelemetryDocumentId'
  | 'kernelTelemetrySummary'
  | 'initialScreenshotUrl'
> {
  if (!timeline) {
    return {
      recordingDocumentId: null,
      kernelTelemetryDocumentId: null,
      kernelTelemetrySummary: null,
      initialScreenshotUrl: null,
    };
  }
  return {
    recordingDocumentId: orNull(timeline.recordingDocumentId),
    kernelTelemetryDocumentId: orNull(timeline.kernelTelemetryDocumentId),
    kernelTelemetrySummary: orNull(timeline.kernelTelemetrySummary),
    initialScreenshotUrl: orNull(timeline.initialScreenshotSignedUrl),
  };
}

function orNull<T>(value: T | null | undefined): T | null {
  if (value == null) return null;
  return value;
}

const SESSION_SUMMARY_TOOL_TYPE = 'agent.session.generate_summary';

/**
 * The one-paragraph answer to "what did this run do?", in order of how
 * deliberate the source is: the agent's own session summary, then the
 * operator's closing message. Action narration is not an outcome. Null when the run
 * left none of these (a run gated off by its start conditions, for instance).
 */
export function runOutcomeText(
  toolCalls: readonly ToolCallRunResponse[],
): string | null {
  for (const toolCall of toolCalls) {
    if (toolCall.toolType !== SESSION_SUMMARY_TOOL_TYPE) continue;
    const summary = toolCall.outputData?.summary;
    if (typeof summary === 'string' && summary.trim()) return summary;
  }
  const finalResponse = operatorFinalResponse([...toolCalls]);
  if (finalResponse) return finalResponse;
  return null;
}

/** The slice of a run timeline the detail card reasons about. */
export interface RunTimelineSlice {
  toolCalls: readonly ToolCallRunResponse[];
  messages: readonly AgentMessageResponse[];
}

/**
 * What the detail card says about a run, decided once so the JSX is a switch:
 *  - `loading`    — timeline not here yet.
 *  - `running`    — still going; the trace speaks for itself.
 *  - `failed`     — show the error.
 *  - `blocked`    — interrupted, then its definition was re-published, so its
 *                   retry started a fresh run instead of resuming it.
 *  - `not-started` — the start gate refused it (a required resource was in
 *                   use, start conditions unmet, …); it never did any work,
 *                   so it must not read as a run that found nothing to do.
 *  - `outcome`    — the run's own summary / closing message.
 *  - `no-summary` — finished with a trace but no prose.
 *  - `no-work`    — finished with nothing recorded at all (typically a
 *                   queue-driven agent that found nothing waiting).
 */
export type RunDetailState =
  | { kind: 'loading' }
  | { kind: 'running' }
  | { kind: 'failed'; message: string }
  | { kind: 'blocked' }
  | { kind: 'not-started'; reason: string | null }
  | { kind: 'outcome'; text: string }
  | { kind: 'no-summary' }
  | { kind: 'no-work' };

export function runHasTrace(timeline: RunTimelineSlice | undefined): boolean {
  return (
    timeline !== undefined &&
    (timeline.messages.length > 0 || timeline.toolCalls.length > 0)
  );
}

export function runDetailState(
  run: Pick<
    AgentRunSummary,
    'status' | 'error' | 'finishReason' | 'notStartedReason'
  >,
  timeline: RunTimelineSlice | undefined,
): RunDetailState {
  if (run.status === 'running') return { kind: 'running' };
  if (run.status === 'failed') {
    return { kind: 'failed', message: run.error?.trim() || 'The run failed.' };
  }
  if (run.status === 'blocked') return { kind: 'blocked' };
  if (runDidNotStart(run)) {
    return { kind: 'not-started', reason: run.notStartedReason ?? null };
  }
  if (timeline === undefined) return { kind: 'loading' };
  const text = runOutcomeText(timeline.toolCalls);
  if (text) return { kind: 'outcome', text };
  return runHasTrace(timeline) ? { kind: 'no-summary' } : { kind: 'no-work' };
}

export interface RunDetailCardModel {
  currentRun: AgentRunSummary;
  state: RunDetailState;
  outcomes: Span[];
  showTrace: boolean;
}

/** Facts the detail card needs from the selected run and its timeline. */
export function buildRunDetailCardModel(
  run: AgentRunSummary,
  timeline: (RunTimelineSlice & { run?: AgentRunSummary }) | undefined,
): RunDetailCardModel {
  const currentRun = timeline?.run ?? run;
  return {
    currentRun,
    state: runDetailState(currentRun, timeline),
    outcomes: taskDetailSpans(
      timeline
        ? buildSpans(
            [...timeline.messages],
            [...timeline.toolCalls],
            currentRun.startedAt,
            currentRun.model,
          )
        : [],
    ),
    showTrace: currentRun.status === 'running' || runHasTrace(timeline),
  };
}

/**
 * Outcome prose is hidden once task results already tell the story. A failure
 * stays visible — unless it is a task's error, which its "Not Finished" row
 * already shows; repeating it at the top as the run's verdict would bury the
 * escalations and completions the run did record.
 */
export function shouldShowRunOutcome(
  outcomes: readonly Span[],
  state: RunDetailState,
): boolean {
  if (state.kind === 'failed')
    return !outcomes.some(
      (span) =>
        isUnfinishedTask(span) &&
        typeof span.toolOutput?.error === 'string' &&
        span.toolOutput.error.trim() === state.message,
    );
  return outcomes.length === 0 || state.kind === 'blocked';
}
